import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
  Button,
  ListItemIcon,
  Chip,
} from "@mui/material";
import biosenseAPI from "../../../api/biosense";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { formatAlertMessage } from "../../../utils/formatAlertMessage";
import { DeviceHub, Person, Work } from "@mui/icons-material";

const typeToIcon = {
  device: <DeviceHub />,
  user: <Person />,
  project: <Work />,
};

const AlertListScreen = ({ setTopBarState }) => {
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchAlerts = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await biosenseAPI.getPendingAlertList({ includeIgnoredAlerts: true });
        setAlerts(res.alerts);
      } catch (err) {
        setError("Failed to load alerts");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAlerts();
  }, []);

  const handleResolve = async (alertId) => {
    try {
      // 調用 API 解決警報
      await biosenseAPI.resolveAlert({ alert_id: alertId });
      // 重新獲取警報列表
      await biosenseAPI.getPendingAlertList({ includeIgnoredAlerts: false }).then((res) => {
        setTopBarState({ alerts: res.alerts });
      }); 
      const res = await biosenseAPI.getPendingAlertList({ includeIgnoredAlerts: true });
      setAlerts(res.alerts);
    } catch (err) {
      console.error("Failed to resolve alert", err);
    }
  };

  const handleIgnore = async (alertId) => {
    try {
      // 調用 API 忽略警報
      await biosenseAPI.ignoreAlert({ alert_id: alertId });
      // 重新獲取警報列表
      const res = await biosenseAPI.getPendingAlertList({ includeIgnoredAlerts: true });
      setAlerts(res.alerts);
    } catch (err) {
      console.error("Failed to ignore alert", err);
    }
  };

  const handleRemoveIgnore = async (alertId) => {
    try {
      // 調用 API 解除忽略警報
      await biosenseAPI.removeIgnoreAlert({ alert_id: alertId }); // 假設有這個 API
      // 重新獲取警報列表
      const res = await biosenseAPI.getPendingAlertList({ includeIgnoredAlerts: true });
      setAlerts(res.alerts);
    } catch (err) {
      console.error("Failed to unignore alert", err);
    }
  };

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
        <CircularProgress sx={{ mt: 1 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
        <Alert severity='error'>{error}</Alert>
      </Box>
    );
  }

  if (!alerts || alerts.length === 0) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
        <Typography variant='body1'>{t("No pending alerts")}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100%" }}>
      <Card sx={{ minHeight: "100%", boxShadow: 4 }}>
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant={"h5"} sx={{ my: 2, mx: 2 }}>
            {t("alertlist.pendingalerts")}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <List>
            {alerts.map((alert, index) => (
              <React.Fragment key={index}>
                <ListItem sx={{ opacity: alert.isIgnored ? 0.5 : 1 }}>
                  <Box sx={{ mr: 1, display: "flex", alignItems: "center", width: 300 }}>
                    <ListItemIcon>{typeToIcon[alert.type] || null}</ListItemIcon>
                    <Chip label={alert?.device_alias || ""} />
                  </Box>
                  <ListItemText
                    primary={formatAlertMessage(t, alert.alert_text)}
                    secondary={dayjs(alert.recorded_at).format("YYYY-MM-DD HH:mm")}
                  />
                  <Box>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => handleResolve(alert.id)}
                      sx={{ mr: 1 }}
                    >
                      {t("alertlist.resolve")}
                    </Button>
                    {alert.isIgnored ? (
                      <Button
                        variant="outlined"
                        color="warning"
                        onClick={() => handleRemoveIgnore(alert.id)}
                      >
                        {t("alertlist.removeignore")}
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => handleIgnore(alert.id)}
                      >
                        {t("alertlist.ignore")}
                      </Button>
                    )}
                  </Box>
                </ListItem>
                {index < alerts.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AlertListScreen;